import {Box, Stack, Typography} from '@mui/material';
import NewTableComponent from '@components/table/NewTable.component';
import {useTranslation} from 'react-i18next';
import {useObservable} from '@ngneat/react-rxjs';
import {organisationsConfigQuery} from '@store/settings/organisationsConfig/organisationsConfig.query';
import {Account} from '@store/settings/account';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import React, {useEffect, useState} from 'react';
import SearchComponent from '@components/inputs/search/Search.component';
import {organisationsConfigService} from '@store/settings/organisationsConfig/organisationsConfig.service';
import {searchOrganisationsConfigEffect} from '@store/settings/organisationsConfig/organisationsConfig.effect';
import {useEffectFn} from '@ngneat/effects-hooks';
import {BlackCheck, GreyCheck} from '@icons/index';
import {OrganisationConfig} from '@store/settings/organisationsConfig/organisationsConfig.model';
import ConfirmModal from '@components/modal/Confirm.modal';

const OrganisationConfigTab = () => {

  const {t} = useTranslation();

  const searchOrganisationsConfig = useEffectFn(searchOrganisationsConfigEffect);

  const [{organisationsConfig, loading}] = useObservable(organisationsConfigQuery.organisationsConfig$);
  const [tablePagination] = useObservable(organisationsConfigQuery.pagination$);
  const [filters] = useObservable(organisationsConfigQuery.filters$);

  const [updates, setUpdates] = useState<{
    organisationId: number;
    isClient?: boolean;
    isSupplier?: boolean;
  } | undefined>(undefined);

  useEffect(() => {
    searchOrganisationsConfig({filters, tablePagination});
  }, [filters, searchOrganisationsConfig, tablePagination]);

  return (
    <>
      <Stack flex={1} width="100%" spacing={2} p="25px">
        <Typography fontSize="30px" fontWeight="700">
          {t('routes.organisations-config')}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
          <Box width="500px">
            <SearchComponent
              value={filters.search}
              handleChange={(value) => {
                organisationsConfigService.setFilters('search')(value);
              }}/>
          </Box>
        </Stack>
        <NewTableComponent
          loading={loading}
          columns={[
            COLUMN_TYPES.TEXT_BOLD({
              field: 'id',
              label: t('settings.organisationConfig.table.id'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, Account>) => params.row.id,
              width: 100
            }),
            COLUMN_TYPES.TEXT({
              field: 'name',
              label: t('settings.organisationConfig.table.name'),
              valueGetter: (params: GridValueGetterParams<any, Account>) => params.row.name,
              fullWidth: true,
              minWidth: 300
            }),
            COLUMN_TYPES.CHECKS({
              field: 'isSupplier',
              label: t('settings.organisationConfig.table.isSupplier'),
              checks: [
                {
                  isChecked: (oc: OrganisationConfig) => oc.isSupplier,
                  checkedIcon: <BlackCheck/>,
                  uncheckedIcon: <GreyCheck/>,
                  handleClick: (oc: OrganisationConfig, checked: boolean) => {
                    if (!checked) {
                      setUpdates({organisationId: oc.id, isSupplier: checked});
                    } else {
                      organisationsConfigService.updateOrganisationConfig(oc.id, {
                        isSupplier: checked,
                      }).subscribe();
                    }

                  },
                },
              ],
              width: 200,
            }),
            COLUMN_TYPES.CHECKS({
              field: 'isClient',
              label: t('settings.organisationConfig.table.isClient'),
              checks: [
                {
                  isChecked: (oc: OrganisationConfig) => oc.isClient,
                  checkedIcon: <BlackCheck/>,
                  uncheckedIcon: <GreyCheck/>,
                  handleClick: (oc: OrganisationConfig, checked: boolean) => {
                    if (!checked) {
                      setUpdates({organisationId: oc.id, isClient: checked});
                    } else {
                      organisationsConfigService.updateOrganisationConfig(oc.id, {
                        isClient: checked,
                      }).subscribe();
                    }
                  },
                },
              ],
              width: 200,
            })
          ]}
          handlePageSizeChange={(value) => {
            organisationsConfigService.setPagination('limit')(value);
            organisationsConfigService.setPagination('page')(1);
          }}
          pageSize={tablePagination.limit}
          handlePageChange={(page) => organisationsConfigService.setPagination('page')(page + 1)}
          page={tablePagination.page - 1}
          disableRowSelection
          totalRows={organisationsConfigQuery.totalRows}
          data={organisationsConfig}
        />
      </Stack>
      {updates && (<ConfirmModal handleClose={() => setUpdates(undefined)} handleValidate={() => {
        organisationsConfigService.updateOrganisationConfig(updates.organisationId, {
          isClient: updates?.isClient,
          isSupplier: updates?.isSupplier
        }).subscribe();
        setUpdates(undefined);
      }} validateButton={t('settings.organisationConfig.confirmRemoveClientSupplier')}
                                 title={t('settings.organisationConfig.title')}
                                 description={t('settings.organisationConfig.description')}/>)}
    </>
  );
};

export default OrganisationConfigTab;
